import { useCallback, useEffect, useState } from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import { Button, Description, Label, Modal, type OverlayTriggerState } from '@heroui/react';
import { AsyncButton } from '../../../components/ui/async-button';
import { cropImageToJpeg } from '../utils/crop-image';

interface ProfilePhotoCropDialogProps {
  state: OverlayTriggerState;
  imageSrc: string | null;
  onConfirm: (file: File) => Promise<void>;
  onCancel: () => void;
}

export function ProfilePhotoCropDialog({
  state,
  imageSrc,
  onConfirm,
  onCancel,
}: ProfilePhotoCropDialogProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!state.isOpen) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setSubmitting(false);
  }, [state.isOpen, imageSrc]);

  const handleCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleClose = () => {
    if (submitting) return;
    onCancel();
  };

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSubmitting(true);
    try {
      const blob = await cropImageToJpeg(imageSrc, croppedAreaPixels);
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      await onConfirm(file);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal state={state}>
      <Modal.Backdrop isDismissable={!submitting} className="z-[400]">
        <Modal.Container placement="center" className="z-[400]">
          <Modal.Dialog className="w-full max-w-md overflow-hidden">
            <Modal.Header>
              <Modal.Heading>Ajustar foto</Modal.Heading>
              <Modal.CloseTrigger />
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-4">
              <Description>Arraste para enquadrar e use o zoom para aproximar o rosto.</Description>
              <div className="relative h-72 w-full overflow-hidden rounded-2xl bg-black">
                {imageSrc ? (
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    roundCropAreaPixels
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={handleCropComplete}
                  />
                ) : null}
              </div>
              <div className="flex flex-col gap-2">
                <Label>Zoom</Label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  disabled={submitting}
                  onChange={(event) => setZoom(Number(event.target.value))}
                  className="w-full accent-current"
                  aria-label="Zoom da foto"
                />
              </div>
            </Modal.Body>
            <Modal.Footer className="flex justify-end gap-2">
              <Button variant="secondary" isDisabled={submitting} onPress={handleClose}>
                Cancelar
              </Button>
              <AsyncButton
                variant="primary"
                isPending={submitting}
                pendingLabel="Salvando"
                isDisabled={!croppedAreaPixels}
                onPress={() => void handleConfirm()}
              >
                Usar esta foto
              </AsyncButton>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
